// client/src/pages/secure/EmployeeDetailPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser, canSeePersonFile } from '../../hooks/useCurrentUser';
import CeRecords from '../../components/secure/CeRecords';
import DocumentManager from '../../components/secure/DocumentManager';
import FormsPanel from '../../components/secure/FormsPanel';

const EMPLOYEE_DOCUMENT_TYPES = ['Offer Letter', 'W4', 'I9', 'Policy Acknowledgement', 'Performance Review', 'Other'];

// --- SSN Section Component ---
const SsnSection = ({ employeeId, hasSsn, isManagingBroker, onSsnChanged }) => {
    const [revealedSsn, setRevealedSsn] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [ssnInput, setSsnInput] = useState('');
    const [error, setError] = useState('');
    const [isBusy, setIsBusy] = useState(false);
    const apiClient = useApiClient();

    if (!isManagingBroker) {
        return (
            <div>
                <p className="text-sm font-medium text-slate-500">SSN</p>
                <p className="text-lg text-slate-900">{hasSsn ? 'On file' : 'Not on file'}</p>
            </div>
        );
    }

    const handleReveal = async () => {
        setError('');
        setIsBusy(true);
        try {
            const data = await apiClient.get(`/api/employees/${employeeId}/ssn`);
            setRevealedSsn(data.ssn);
        } catch (err) {
            setError(err.message || 'Failed to retrieve SSN.');
        } finally {
            setIsBusy(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setError('');
        setIsBusy(true);
        try {
            await apiClient.put(`/api/employees/${employeeId}/ssn`, { ssn: ssnInput });
            setIsEditing(false);
            setSsnInput('');
            setRevealedSsn(null);
            onSsnChanged();
        } catch (err) {
            setError(err.message || 'Failed to save SSN.');
        } finally {
            setIsBusy(false);
        }
    };

    return (
        <div>
            <p className="text-sm font-medium text-slate-500">SSN</p>
            {!isEditing ? (
                <div className="flex items-center gap-3">
                    <p className="text-lg text-slate-900">
                        {revealedSsn ? revealedSsn : (hasSsn ? '•••-••-••••' : 'Not on file')}
                    </p>
                    {hasSsn && !revealedSsn && (
                        <button onClick={handleReveal} disabled={isBusy} className="text-sm text-indigo-600 hover:underline">Reveal</button>
                    )}
                    {revealedSsn && (
                        <button onClick={() => setRevealedSsn(null)} className="text-sm text-indigo-600 hover:underline">Hide</button>
                    )}
                    <button onClick={() => setIsEditing(true)} className="text-sm text-indigo-600 hover:underline">{hasSsn ? 'Update' : 'Add'}</button>
                </div>
            ) : (
                <form onSubmit={handleSave} className="flex items-center gap-2 mt-1">
                    <input
                        type="text"
                        value={ssnInput}
                        onChange={(e) => setSsnInput(e.target.value)}
                        placeholder="123-45-6789"
                        className="p-2 border rounded"
                        required
                    />
                    <button type="submit" disabled={isBusy} className="px-3 py-2 text-sm bg-slate-800 text-white rounded disabled:bg-indigo-400">Save</button>
                    <button type="button" onClick={() => { setIsEditing(false); setSsnInput(''); }} className="px-3 py-2 text-sm bg-gray-200 rounded">Cancel</button>
                </form>
            )}
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
        </div>
    );
};

// --- NEW CODE START ---

// --- Helper Component for Close Icon ---
const XIcon = () => (
    // FIX: Corrected the xmlns attribute for the SVG.
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

// --- Edit Employee Modal Component ---
const EditEmployeeModal = ({ employee, isOpen, onSave, onCancel }) => {
    const [formData, setFormData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const apiClient = useApiClient();

    useEffect(() => {
        if (employee) {
            setFormData({
                ...employee,
                license_expiration_date: employee.license_expiration_date ? employee.license_expiration_date.slice(0, 10) : '',
            });
        }
    }, [employee]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({ ...prevData, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setError('');
        try {
            const updatedEmployee = await apiClient.put(`/api/employees/${employee.employee_id}`, formData);
            onSave(updatedEmployee);
        } catch (err) {
            setError(err.message || 'Failed to save changes.');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen || !formData) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-lg">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-slate-800">Edit Employee Details</h2>
                    <button onClick={onCancel} className="text-gray-500 hover:text-gray-800">
                        <XIcon />
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="first_name" className="block text-sm font-medium text-slate-700">First Name</label>
                            <input type="text" name="first_name" id="first_name" value={formData.first_name} onChange={handleChange} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label htmlFor="last_name" className="block text-sm font-medium text-slate-700">Last Name</label>
                            <input type="text" name="last_name" id="last_name" value={formData.last_name} onChange={handleChange} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="license_number" className="block text-sm font-medium text-slate-700">License Number (optional)</label>
                            <input type="text" name="license_number" id="license_number" value={formData.license_number || ''} onChange={handleChange} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label htmlFor="license_expiration_date" className="block text-sm font-medium text-slate-700">License Expiration Date</label>
                            <input type="date" name="license_expiration_date" id="license_expiration_date" value={formData.license_expiration_date || ''} onChange={handleChange} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="job_title" className="block text-sm font-medium text-slate-700">Job Title</label>
                        <input type="text" name="job_title" id="job_title" value={formData.job_title} onChange={handleChange} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                    </div>
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-slate-700">Email</label>
                        <input type="email" name="email" id="email" value={formData.email || ''} onChange={handleChange} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                    </div>
                    <div>
                        <label htmlFor="phone_number" className="block text-sm font-medium text-slate-700">Phone</label>
                        <input type="tel" name="phone_number" id="phone_number" value={formData.phone_number || ''} onChange={handleChange} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                    </div>
                     <div>
                        <label htmlFor="status" className="block text-sm font-medium text-slate-700">Status</label>
                        <select name="status" id="status" value={formData.status} onChange={handleChange} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md">
                            <option value="Active">Active</option>
                            <option value="On Leave">On Leave</option>
                            <option value="Terminated">Terminated</option>
                        </select>
                    </div>
                    {getCurrentUser()?.isManagingBroker && (
                        <div>
                            <label htmlFor="staff_role" className="block text-sm font-medium text-slate-700">Staff Access Level</label>
                            <select name="staff_role" id="staff_role" value={formData.staff_role || 'Staff'} onChange={handleChange} className="mt-1 block w-full p-2 border border-gray-300 rounded-md">
                                <option value="Staff">Staff (view-only)</option>
                                <option value="Office Manager">Office Manager</option>
                                <option value="Bookkeeper">Bookkeeper</option>
                                <option value="Read Only">Read only (can look, cannot change anything)</option>
                            </select>
                        </div>
                    )}
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                        <button type="submit" disabled={isSaving} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- NEW CODE END ---


// --- Main Employee Detail Page Component ---
const EmployeeDetailPage = () => {
    const { id } = useParams();
    const [employee, setEmployee] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // --- NEW CODE START ---
    const [isEditModalOpen, setIsEditModalOpen] = useState(false); // State for edit modal
    // --- NEW CODE END ---
    const apiClient = useApiClient();
    const currentUser = getCurrentUser();

    const fetchEmployeeData = useCallback(async () => {
        try {
            const employeeData = await apiClient.get(`/api/employees/${id}`);
            setEmployee(employeeData);
        } catch (err) {
            setError(err.message || 'Failed to fetch employee data.');
        } finally {
            setIsLoading(false);
        }
    }, [id, apiClient]);

    useEffect(() => {
        fetchEmployeeData();
    }, [fetchEmployeeData]);

    // --- NEW CODE START ---
    const handleEmployeeUpdated = (updatedEmployee) => {
        setEmployee(updatedEmployee); // Update state with new employee data
        setIsEditModalOpen(false); // Close modal
    };
    // --- NEW CODE END ---

    if (isLoading) return <div className="p-8">Loading employee details...</div>;
    if (error) return <div className="p-8 text-red-500">{error}</div>;
    if (!employee) return <div className="p-8">Employee not found.</div>;

    return (
        <>
            {/* --- NEW CODE START --- */}
            <EditEmployeeModal
                isOpen={isEditModalOpen}
                onCancel={() => setIsEditModalOpen(false)}
                onSave={handleEmployeeUpdated}
                employee={employee}
            />
            {/* --- NEW CODE END --- */}
            <div>
                <div className="mb-6">
                    <Link to="/dashboard/employees" className="text-indigo-600 hover:text-indigo-900">&larr; Back to Employee List</Link>
                </div>

                <div className="bg-white p-8 rounded-lg shadow mb-8">
                    {/* --- NEW CODE START --- */}
                    <div className="flex justify-between items-start">
                        <div>
                            <h1 className="text-3xl font-bold text-slate-800">{employee.first_name} {employee.last_name}</h1>
                            <p className="text-lg text-slate-600">{employee.job_title}</p>
                        </div>
                        <button onClick={() => setIsEditModalOpen(true)} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">
                            Edit Employee
                        </button>
                    </div>
                    {/* --- NEW CODE END --- */}
                    {/* MODIFIED: Removed the h1 and p tags from here as they are now inside the flex container above */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                        <div><p className="text-sm font-medium text-slate-500">Email</p><p className="text-lg text-slate-900">{employee.email || 'N/A'}</p></div>
                        <div><p className="text-sm font-medium text-slate-500">Phone</p><p className="text-lg text-slate-900">{employee.phone_number || 'N/A'}</p></div>
                        <div><p className="text-sm font-medium text-slate-500">Status</p><p className="text-lg text-slate-900">{employee.status}</p></div>
                        <div><p className="text-sm font-medium text-slate-500">License Number</p><p className="text-lg text-slate-900">{employee.license_number || 'N/A'}</p></div>
                        <div><p className="text-sm font-medium text-slate-500">License Expiration</p><p className="text-lg text-slate-900">{employee.license_expiration_date ? new Date(employee.license_expiration_date).toLocaleDateString() : 'N/A'}</p></div>
                        <SsnSection
                            employeeId={id}
                            hasSsn={!!employee.has_ssn}
                            isManagingBroker={!!currentUser?.isManagingBroker}
                            onSsnChanged={fetchEmployeeData}
                        />
                    </div>
                </div>

                {/* Personnel files: the employee, the office manager, the managing broker, an examiner. */}
                {canSeePersonFile(currentUser, { employeeId: id }) ? (
                    <DocumentManager relatedId={id} relatedType="employee" documentTypes={EMPLOYEE_DOCUMENT_TYPES} />
                ) : (
                    <p className="mt-8 text-sm text-slate-500">This person's personnel documents are visible to them, the office manager and the managing broker.</p>
                )}

                <CeRecords employeeId={id} />

                {/* Employment and policy acknowledgements, signed electronically. */}
                <div className="mt-8">
                    <FormsPanel
                        subjectType="employee"
                        subjectId={id}
                        subjectName={employee ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim() : null}
                    />
                </div>
            </div>
        </>
    );
};

export default EmployeeDetailPage;
